import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiUsers,
  FiHeart,
  FiTrendingUp,
  FiAlertCircle,
  FiChevronRight,
  FiSearch,
  FiLoader,
} from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import { getZoneColor } from "../../utils/zoneUtils";

interface Child {
  id: number;
  name: string;
  age: number;
  grade: string;
  school: string;
  currentMood: string;
  currentZone: string;
  lastLogged: string;
  moodStreak: number;
  totalMoods: number;
  needsAttention: boolean;
  email: string;
  phone?: string;
}

const Children: React.FC = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [children, setChildren] = useState<Child[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string | null): number => {
    if (!dateOfBirth) return 0;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Format time ago
  const formatTimeAgo = (date: string | null): string => {
    if (!date) return "Never";
    const now = new Date();
    const logDate = new Date(date);
    const diffMs = now.getTime() - logDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    return logDate.toLocaleDateString();
  };

  // Fetch children from backend
  useEffect(() => {
    const fetchChildren = async () => {
      if (!user?.email) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch students where parentEmail matches logged-in parent's email
        const response = await axios.get(`${API_BASE_URL}/students`, {
          params: {
            status: "active",
            parentEmail: user.email,
          },
        });

        if (response.data && Array.isArray(response.data)) {
          // Fetch mood logs for each child to get current status
          const childrenWithMoodData = await Promise.all(
            response.data.map(async (student: any) => {
              try {
                // Get latest mood log for this student
                const moodLogsResponse = await axios.get(`${API_BASE_URL}/student-mood-logs`, {
                  params: {
                    studentId: student.id,
                    status: true,
                  },
                });

                const moodLogs = moodLogsResponse.data || [];
                const latestLog = moodLogs.length > 0 ? moodLogs[0] : null;

                // Calculate mood streak (consecutive days with mood logs)
                let streak = 0;
                if (moodLogs.length > 0) {
                  const sortedLogs = [...moodLogs].sort(
                    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
                  );
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  for (let i = 0; i < sortedLogs.length; i++) {
                    const logDate = new Date(sortedLogs[i].date);
                    logDate.setHours(0, 0, 0, 0);
                    const daysDiff = Math.floor((today.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));

                    if (daysDiff === i) {
                      streak++;
                    } else {
                      break;
                    }
                  }
                }

                // Determine if needs attention (based on recent negative zones)
                const recentLogs = moodLogs.slice(0, 5);
                const needsAttention = recentLogs.some((log: any) => {
                  const zone = (log.calculatedZone || "").toLowerCase();
                  return (
                    zone.includes("red") ||
                    zone.includes("yellow") ||
                    zone.includes("brown")
                  );
                });

                return {
                  id: student.id,
                  name: student.name,
                  age: calculateAge(student.dateOfBirth),
                  grade: student.class?.name || "N/A",
                  school: student.school?.name || "N/A",
                  currentMood: latestLog?.calculatedEmotion || "N/A",
                  currentZone: latestLog?.calculatedZone
                    ? `${latestLog.calculatedZone} Zone`
                    : "N/A",
                  lastLogged: formatTimeAgo(latestLog?.date || null),
                  moodStreak: streak,
                  totalMoods: moodLogs.length,
                  needsAttention,
                  email: student.email,
                  phone: student.phone || undefined,
                };
              } catch (err) {
                console.error(`Error fetching mood data for student ${student.id}:`, err);
                return {
                  id: student.id,
                  name: student.name,
                  age: calculateAge(student.dateOfBirth),
                  grade: student.class?.name || "N/A",
                  school: student.school?.name || "N/A",
                  currentMood: "N/A",
                  currentZone: "N/A",
                  lastLogged: "Never",
                  moodStreak: 0,
                  totalMoods: 0,
                  needsAttention: false,
                  email: student.email,
                  phone: student.phone || undefined,
                };
              }
            })
          );

          setChildren(childrenWithMoodData);
        }
      } catch (err: any) {
        console.error("Error fetching children:", err);
        setError("Failed to load children. Please try again.");
        setChildren([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChildren();
  }, [user]);

  
  const filteredChildren = children.filter((child) =>
    child.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    child.school.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // getZoneColor imported from ../../utils/zoneUtils — single source of truth

  return (
    <div className="p-8 bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">My Children</h1>
        <p className="text-slate-600">Manage and monitor your children's profiles</p>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border border-slate-200/80">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Search by name or school..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:border-[#1ecab8]"
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200/80">
          <FiLoader className="mx-auto text-[#1ecab8] mb-4 animate-spin" size={48} />
          <p className="text-slate-600">Loading children...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200/80">
          <FiAlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#1ecab8] text-white rounded-lg hover:bg-[#1bb8a6] transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Children Grid */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredChildren.map((child) => (
          <div
            key={child.id}
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start gap-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-semibold text-white"
                  style={{ background: getZoneColor(child.currentZone).color }}
                >
                  {child.name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-semibold text-slate-900">
                      {child.name}
                    </h3>
                    {child.needsAttention && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                        Needs Attention
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-1">
                    {child.age} years old • {child.grade}
                  </p>
                  <p className="text-sm text-slate-500">{child.school}</p>
                </div>
              </div>
            </div>

            {/* Current Status */}
            <div className="mb-4 p-4 rounded-lg" style={{ background: `${getZoneColor(child.currentZone).color}15` }}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Current Status</span>
                <span
                  className="text-sm font-semibold px-2 py-1 rounded"
                  style={{ background: getZoneColor(child.currentZone).color, color: "white" }}
                >
                  {child.currentZone}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                Mood: <strong>{child.currentMood}</strong> • Last logged: {child.lastLogged}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Mood Streak</div>
                <div className="text-lg font-bold text-slate-900">{child.moodStreak} days</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Total Logs</div>
                <div className="text-lg font-bold text-slate-900">{child.totalMoods}</div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t border-slate-200">
              <Link
                to={`/parent/children/${child.id}/mood-tracking`}
                className="flex-1 bg-[#1ecab8] text-white text-center py-2 rounded-lg font-medium hover:bg-[#1bb8a6] transition-colors flex items-center justify-center gap-2"
              >
                <FiTrendingUp size={18} />
                View Mood Tracking
              </Link>
              <Link
                to={`/parent/children/${child.id}/profile`}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
              >
                <FiUsers size={18} />
                Profile
              </Link>
            </div>
          </div>
        ))}
        </div>
      )}

      {!loading && !error && filteredChildren.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200/80">
          <FiUsers size={48} className="mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600">No children found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default Children;

